import React, { useEffect, useState } from "react"
import { Loader2, Save, Bell } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Switch } from "src/components/ui/switch"
import { Label } from "src/components/ui/label"
import { Checkbox } from "src/components/ui/checkbox"
import { Input } from "src/components/ui/input"
import { useToast } from "src/components/ui/toast"
import { cn } from "src/utils/cn"
import { fetchNotificationSettings } from "../api/notificationSettingsApi"
import { useUpdateNotificationSettings } from "../hooks/useNotificationSettings"

const EVENTS = [
  { value: "site_visit", label: "Site Visits", hint: "Scheduled visits you host" },
  { value: "task", label: "Tasks", hint: "Tasks assigned to you" },
  { value: "reminder", label: "Reminders", hint: "Follow-ups and reach-outs" },
]

const LEAD_TIMES = [
  { value: "morning", label: "Morning digest", hint: "A summary of your day" },
  { value: "one_hour", label: "1 hour before", hint: "Ahead of the event" },
  { value: "five_min", label: "5 minutes before", hint: "Just before it starts" },
]

const CHANNELS = [
  { value: "in_app", label: "In-app", hint: "Notification bell" },
  { value: "push", label: "Push", hint: "Device & browser" },
]

export default function NotificationSettingsForm() {
  const { toast } = useToast()
  const { mutate: save, isPending: saving } = useUpdateNotificationSettings()

  const [loading, setLoading] = useState(true)
  const [canEditOrg, setCanEditOrg] = useState(false)
  const [scope, setScope] = useState("user") // "user" | "organization"
  const [orgSettings, setOrgSettings] = useState(null)
  const [userSettings, setUserSettings] = useState(null)

  useEffect(() => {
    fetchNotificationSettings()
      .then((data) => {
        setOrgSettings(data.organization)
        // When a user hasn't customized anything, seed the editor from the
        // effective (org-merged) view so the toggles reflect what they'll get.
        setUserSettings(data.effective)
        setCanEditOrg(!!data.can_edit_organization)
      })
      .catch(() =>
        toast({ variant: "destructive", title: "Error", description: "Failed to load settings." })
      )
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const settings = scope === "organization" ? orgSettings : userSettings
  const setSettings = scope === "organization" ? setOrgSettings : setUserSettings

  function updateField(key, value) {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  function toggleNested(group, key, value) {
    setSettings((prev) => ({
      ...prev,
      [group]: { ...(prev[group] || {}), [key]: value },
    }))
  }

  async function handleSave() {
    try {
      const updated = await save(settings, scope)
      setOrgSettings(updated.organization)
      // Re-seed the per-user editor from the fresh effective merge so an org
      // default change (or a user override) is reflected immediately.
      setUserSettings(updated.effective)
      toast({ title: "Settings saved", description: "Notification settings updated." })
    } catch (errors) {
      toast({ variant: "destructive", title: "Error", description: errors?.[0] || "Failed to save settings." })
    }
  }

  const master = settings?.enabled

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading settings…
      </div>
    )
  }
  if (!settings) return null

  return (
    <div className="mx-auto max-w-[720px]">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
          <Bell className="h-4.5 w-4.5 text-slate-600" />
        </div>
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">Reminder Notifications</h2>
          <p className="text-[12px] text-slate-500">
            Push reminders for site visits, tasks, and follow-ups — on web and mobile.
          </p>
        </div>
      </div>

      {/* Scope switch — admins can edit org-wide defaults or their own prefs. */}
      {canEditOrg && (
        <div className="mb-4 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {[
            { id: "user", label: "My preferences" },
            { id: "organization", label: "Organization defaults" },
          ].map((tab) => (
            <Button
              key={tab.id}
              variant="ghost"
              onClick={() => setScope(tab.id)}
              className={cn(
                "h-auto rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors",
                scope === tab.id ? "bg-white text-slate-900 shadow-sm hover:bg-white" : "text-slate-500 hover:bg-transparent hover:text-slate-700"
              )}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white">
        {/* Master enable */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <p className="text-[14px] font-medium text-slate-900">Reminder notifications</p>
            <p className="text-[12px] text-slate-500">
              {scope === "organization"
                ? "Turn reminders on or off for everyone in the organization."
                : "Turn your reminder notifications on or off."}
            </p>
          </div>
          <Switch checked={!!master} onCheckedChange={(c) => updateField("enabled", c)} />
        </div>

        <div className={master ? "" : "pointer-events-none opacity-50"}>
          {/* Events */}
          <Section title="Which events" subtitle="Choose what you want to be reminded about.">
            <div className="space-y-3">
              {EVENTS.map((ev) => (
                <ToggleRow
                  key={ev.value}
                  label={ev.label}
                  hint={ev.hint}
                  checked={!!settings.events?.[ev.value]}
                  onChange={(c) => toggleNested("events", ev.value, c)}
                />
              ))}
            </div>
          </Section>

          {/* Lead times */}
          <Section title="When" subtitle="How far ahead we remind you.">
            <div className="space-y-3">
              {LEAD_TIMES.map((lt) => (
                <ToggleRow
                  key={lt.value}
                  label={lt.label}
                  hint={lt.hint}
                  checked={!!settings.lead_times?.[lt.value]}
                  onChange={(c) => toggleNested("lead_times", lt.value, c)}
                >
                  {lt.value === "morning" && settings.lead_times?.morning && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-[12px] text-slate-500">Send at</span>
                      <Input
                        type="time"
                        value={settings.morning_hour || "08:00"}
                        onChange={(e) => updateField("morning_hour", e.target.value)}
                        className="w-28 text-[13px]"
                      />
                    </div>
                  )}
                </ToggleRow>
              ))}
            </div>
          </Section>

          {/* Channels */}
          <Section title="Channels" subtitle="Where reminders are delivered." last>
            <div className="flex flex-wrap gap-4">
              {CHANNELS.map((ch) => (
                <label key={ch.value} className="flex items-center gap-2 text-[13px] text-slate-700">
                  <Checkbox
                    checked={!!settings.channels?.[ch.value]}
                    onCheckedChange={(c) => toggleNested("channels", ch.value, c)}
                  />
                  <span>
                    {ch.label}
                    <span className="ml-1 text-[11px] text-slate-400">· {ch.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </Section>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save changes
        </Button>
      </div>
    </div>
  )
}

function Section({ title, subtitle, children, last }) {
  return (
    <div className={cn("px-6 py-4", !last && "border-b border-slate-100")}>
      <Label className="text-[13px] font-medium text-slate-700">{title}</Label>
      {subtitle && <p className="mb-3 text-[12px] text-slate-400">{subtitle}</p>}
      {children}
    </div>
  )
}

function ToggleRow({ label, hint, checked, onChange, children }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[13px] font-medium text-slate-800">{label}</p>
          {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
        </div>
        <Switch checked={checked} onCheckedChange={onChange} />
      </div>
      {children}
    </div>
  )
}
